import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import React, { useCallback, useEffect } from "react";
import PrivateComponentLoadingPage from "../../../LoadingPage/PrivateComponentLoadingPage";
import PaymentsAppLayout from "../../../../Components/Molecules/PaymentsAppLayout";
import { ReactComponent as XeroLogo } from "../../../../Assets/SettingsPages/IntegrationPage/XeroLogo.svg";
import { useIntegrationStore } from "../../../../Store/Integration";
import { XeroSettingsRequest } from "../../../../Contracts/Integration";
import { PaymentRoutes } from "../../../../Contracts/Routes";
import { useNavigate } from "react-router-dom";

const XeroSettingsPage: React.FC = () => {
  const {
    xeroMetaDataView,
    getXeroMetaData,
    getSavedXeroMetaData,
    savedXeroMappingView,
    createOrUpdateXeroSettings
  } = useIntegrationStore();
  const { getAccessTokenSilently } = useAuth0();
  const [xeroSettings, setXeroSettings] = React.useState<XeroSettingsRequest>({
    configuration: []
  });
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [requiredFieldsMissingError, setRequiredFieldsMissingError] =
    React.useState<boolean>(false);
  const navigate = useNavigate();

  const refreshThings = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      getXeroMetaData(getAccessTokenSilently),
      getSavedXeroMetaData(getAccessTokenSilently)
    ]);
    setIsLoading(false);
  }, [getAccessTokenSilently, getXeroMetaData, getSavedXeroMetaData]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings]);

  useEffect(() => {
    if (savedXeroMappingView) {
      const newConfiguration = savedXeroMappingView.configuration.map(config => ({
        zomentum_key: config.key,
        xero_key: config.value
      }));
      setXeroSettings(prevSettings => ({
        ...prevSettings,
        configuration: newConfiguration
      }));
    }
  }, [savedXeroMappingView]);

  const onSettingsSave = async () => {
    if (validateRequiredFields()) {
      setRequiredFieldsMissingError(true);
    }
    setRequiredFieldsMissingError(false);
    setIsSaving(true);
    await createOrUpdateXeroSettings(getAccessTokenSilently, xeroSettings);
    setIsSaving(false);
    navigate(PaymentRoutes.SETTINGS_INTEGRATION);
  };

  const setXeroConfiguration = (zomentum_key: string, xero_key: string) => {
    const existingConfigIndex = xeroSettings.configuration.findIndex(
      config => config.zomentum_key === zomentum_key
    );

    const newConfiguration =
      existingConfigIndex !== -1
        ? xeroSettings.configuration.map((config, index) =>
            index === existingConfigIndex ? { zomentum_key, xero_key } : config
          )
        : xeroSettings.configuration.concat({
            zomentum_key,
            xero_key
          });

    setXeroSettings({
      ...xeroSettings,
      configuration: newConfiguration
    });
  };

  const getConfigValue = (key: string | undefined) =>
    xeroSettings.configuration.find(config => config.zomentum_key === key)?.xero_key || "";

  const supplierKey = xeroMetaDataView?.suppliers.zomentum_payment_key.key;
  const incomeAccountKey = xeroMetaDataView?.income_accounts.zomentum_payment_key.key;
  const expenseAccountKey = xeroMetaDataView?.expense_accounts.zomentum_payment_key.key;
  const payoutBankAccountKey = xeroMetaDataView?.payout_bank_accounts.zomentum_payment_key.key;

  const supplierValue = getConfigValue(supplierKey);
  const incomeAccountValue = getConfigValue(incomeAccountKey);
  const expenseAccountValue = getConfigValue(expenseAccountKey);
  const payoutBankAccountValue = getConfigValue(payoutBankAccountKey);

  const validateRequiredFields = (): boolean => {
    const requiredFields = [
      supplierValue,
      incomeAccountValue,
      expenseAccountValue,
      payoutBankAccountValue
    ];
    return requiredFields.some(field => field === "");
  };

  return (
    <PaymentsAppLayout noPadding>
      <div className="grid md:grid-cols-5 grid-cols-1">
        <div className="col-span-1 border-r p-6 overflow-hidden">
          <XeroLogo />
          <div className="mt-2">
            Connect Xero Online with Zomentum Payments to sync your clients, auto-collect invoices
            and record payments and fees.
          </div>
        </div>
        <div className="md:col-span-4 col-span-1">
          <div className="md:h-[calc(100vh-11.3rem)] overflow-auto">
            <div className="p-6 border-b">
              <div className="flex flex-col space-y-2 max-w-2xl">
                <div className="font-semibold">Supplier and Expense Account</div>
                <div>
                  Zomentum Payments charges a processing fee for every transaction. We generate
                  expense transactions to reconcile these fees in order to balance your books. These
                  transactions are recorded in the Expense Account and linked to the Supplier you
                  choose here.
                </div>
                <label className="pt-2">
                  Select the Xero Online Supplier to associate with Zomentum Payments
                  <sup className="text-red-600">*</sup>
                </label>
                {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                {!isLoading && (
                  <select
                    className={`border rounded px-2 py-2 ${
                      supplierValue === "" ? "text-gray-400" : ""
                    }`}
                    value={supplierValue}
                    onChange={e => supplierKey && setXeroConfiguration(supplierKey, e.target.value)}
                  >
                    <option value="" disabled>
                      Select Supplier
                    </option>
                    {(xeroMetaDataView?.suppliers.xero_options || []).map(supplier => (
                      <option key={supplier.key} value={supplier.key}>
                        {supplier.display_value}
                      </option>
                    ))}
                  </select>
                )}

                <label className="pt-2">
                  Select the Expense Account for fees charged by Zomentum Payments
                  <sup className="text-red-600">*</sup>
                </label>
                {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                {!isLoading && (
                  <select
                    className={`border rounded px-2 py-2 ${
                      expenseAccountValue === "" ? "text-gray-400" : ""
                    }`}
                    value={expenseAccountValue}
                    onChange={e =>
                      expenseAccountKey && setXeroConfiguration(expenseAccountKey, e.target.value)
                    }
                  >
                    <option value="" disabled>
                      Select Expense Account
                    </option>
                    {(xeroMetaDataView?.expense_accounts.xero_options || []).map(expenseAccount => (
                      <option key={expenseAccount.key} value={expenseAccount.key}>
                        {expenseAccount.display_value}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="p-6 border-b">
              <div className="flex flex-col space-y-2 max-w-2xl">
                <div className="font-semibold">Payout Bank Account</div>
                <div>
                  Zomentum Payments will transfer your collected funds to the bank account you set
                  up for payouts during the Customer Identification process. This deposit will be
                  recorded automatically in the configured Bank Account in Xero Online.
                </div>
                <label className="pt-2">
                  Select the Bank Account where the payout will be deposited to
                  <sup className="text-red-600">*</sup>
                </label>
                {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                {!isLoading && (
                  <select
                    className={`border rounded px-2 py-2 ${
                      payoutBankAccountValue === "" ? "text-gray-400" : ""
                    }`}
                    value={payoutBankAccountValue}
                    onChange={e =>
                      payoutBankAccountKey &&
                      setXeroConfiguration(payoutBankAccountKey, e.target.value)
                    }
                  >
                    <option value="" disabled>
                      Select Payout Bank Account
                    </option>
                    {(xeroMetaDataView?.payout_bank_accounts.xero_options || []).map(
                      payoutBankAccount => (
                        <option key={payoutBankAccount.key} value={payoutBankAccount.key}>
                          {payoutBankAccount.display_value}
                        </option>
                      )
                    )}
                  </select>
                )}
              </div>
            </div>
            <div className="p-6">
              <div className="flex flex-col space-y-2 max-w-2xl">
                <div className="font-semibold">Income Account</div>
                <div>
                  If you collect surcharges on your payments, these surcharges are recorded as
                  income in your Xero Online account when we pay out your collected funds.
                </div>
                <label className="pt-2">
                  Select Income Account to reconcile surcharges
                  <sup className="text-red-600">*</sup>
                </label>
                {isLoading && <div className="w-full h-8 animate-pulse bg-gray-200" />}
                {!isLoading && (
                  <select
                    className={`border rounded px-2 py-2 ${
                      incomeAccountValue === "" ? "text-gray-400" : ""
                    }`}
                    value={incomeAccountValue}
                    onChange={e =>
                      incomeAccountKey && setXeroConfiguration(incomeAccountKey, e.target.value)
                    }
                  >
                    <option value="" disabled>
                      Select Income Account
                    </option>
                    {(xeroMetaDataView?.income_accounts.xero_options || []).map(incomeAccount => (
                      <option key={incomeAccount.key} value={incomeAccount.key}>
                        {incomeAccount.display_value}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
          <div className="p-6 border-t flex space-x-4 items-center">
            <button
              className={`bg-blue-500 px-6 py-1 text-white rounded hover:bg-blue-400 ${
                isSaving && "opacity-50 cursor-progress"
              }`}
              disabled={isSaving}
              onClick={onSettingsSave}
            >
              Save
            </button>
            {requiredFieldsMissingError && (
              <div className="text-red-600">You must complete the mappings before proceeding</div>
            )}
          </div>
        </div>
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(XeroSettingsPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
